import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import type { ReadableSpan, Span, SpanProcessor } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { metrics, SpanKind, type Meter, type Tracer } from '@opentelemetry/api';
import {
    CompositePropagator,
    W3CBaggagePropagator,
    W3CTraceContextPropagator,
} from '@opentelemetry/core';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { storeToRefs } from 'pinia';
import { watch, ref, computed } from 'vue';
import { appSettings, getApiHost } from '@/appSettings';
import { NavigationContextManager } from '@/composables/opentelemetry/navigationContext';
import { abortPageLoad, currentRoutePattern, trackRequestEnd, trackRequestStart } from '@/composables/opentelemetry/pageLoadSpan';
import { getTracer, SERVICE_NAME } from '@/composables/opentelemetry/tracer';
import { currentInstanceName, currentSubdomain, currentUserId, setSpanIdentity } from '@/composables/opentelemetry/identity';
import { templateHttpSpanName } from '@/composables/opentelemetry/templating';
import { DropNoisySpansSampler } from '@/composables/opentelemetry/sampling';

type UseOpentelemetry = {
    init: () => void;
    getTracer: (name?: string) => Tracer;
    getMeter: (name?: string) => Meter;
};

let tracerProvider: WebTracerProvider | null = null;
let meterProvider: MeterProvider | null = null;
let unregisterInstrumentations: (() => void) | null = null;
let isInitialized = false;
let restoreFetch: (() => void) | null = null;

/** The request URL, whichever of the three shapes fetch was handed. */
function requestUrl(input: RequestInfo | URL): string {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input.url;
}

/**
 * The OTLP exporter builds its own fetch call and omits `credentials`, so cookies are
 * not sent when the collector is on another origin (every *-story deploy) and ingest
 * answers 403. Installed before the instrumentations so their `__original` unwrapping
 * still reaches this. Same-origin behaviour is unchanged — cookies already go there.
 */
function sendCollectorRequestsWithCredentials(collectorPrefix: string): void {
    if (restoreFetch != null) return;

    const originalFetch = globalThis.fetch;

    globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        if (requestUrl(input).startsWith(collectorPrefix)) {
            return originalFetch(input, { ...init, credentials: 'include' });
        }

        return originalFetch(input, init);
    };

    restoreFetch = (): void => {
        globalThis.fetch = originalFetch;
    };
}

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? '0.0.0';

// A fetch span is ~1.4 kB of OTLP JSON once its network events are dropped, so 50 keeps
// an export near 70 kB — under the collector's 100 kB body limit with room to spare.
const MAX_SPANS_PER_EXPORT = 50;

/** Stamps user/tenant identity on spans as they start. */
class IdentitySpanProcessor implements SpanProcessor {
    public onStart(span: Span): void {
        // Deliberately no email: these reach every span, not one.
        const userId = currentUserId();
        const subdomain = currentSubdomain();
        const instanceName = currentInstanceName();

        if (userId != null) span.setAttribute('user.id', userId);
        if (subdomain != null) span.setAttribute('instance.subdomain', subdomain);
        if (instanceName != null) span.setAttribute('instance.name', instanceName);
    }

    public onEnd(): void { /* nothing to do */ }
    public async forceFlush(): Promise<void> { return Promise.resolve(); }
    public async shutdown(): Promise<void> { return Promise.resolve(); }
}

/**
 * fetch and XHR name their spans after the method alone, so every REST call in SigNoz
 * reads 'POST' and says nothing about intent. Renaming from the URL is done here rather
 * than per-instrumentation because both put the URL on the span before onStart.
 */
class HttpSpanNamingProcessor implements SpanProcessor {
    public onStart(span: Span): void {
        if (span.kind !== SpanKind.CLIENT) return;

        const url = span.attributes['http.url'] ?? span.attributes['url.full'];
        const method = span.attributes['http.method'] ?? span.attributes['http.request.method'];

        if (typeof url !== 'string' || typeof method !== 'string') return;

        span.updateName(templateHttpSpanName(method, url));

        const route = currentRoutePattern();
        if (route != null) span.setAttribute('page.route', route);
    }

    public onEnd(): void { /* nothing to do */ }
    public async forceFlush(): Promise<void> { return Promise.resolve(); }
    public async shutdown(): Promise<void> { return Promise.resolve(); }
}

/** Feeds outgoing HTTP into the page-load span's in-flight count. */
class RequestTrackingSpanProcessor implements SpanProcessor {
    public onStart(span: Span): void {
        if (span.kind === SpanKind.CLIENT) trackRequestStart();
    }

    public onEnd(span: ReadableSpan): void {
        if (span.kind === SpanKind.CLIENT) trackRequestEnd();
    }

    public async forceFlush(): Promise<void> { return Promise.resolve(); }
    public async shutdown(): Promise<void> { return Promise.resolve(); }
}

/**
 * Provides OpenTelemetry tracing and metrics functionality.
 * Initializes telemetry when user logs in and shuts down when they log out.
 */
export const useOpentelemetry = (): UseOpentelemetry => {
    const loginStore = useLoginStore();
    const { isLoggedIn, userData, selectedInstance, instance } = storeToRefs(loginStore);

    const loginUser = computed(() => userData.value?.loginUser ?? null);
    const isEnabled = ref(false);

    function getHeaders(): Record<string, string> {
        return {
            'Content-Type': 'application/json',
            'x-subdomain': instance.value ?? '',
        };
    }

    function createTracerProvider(): WebTracerProvider {
        const resource = resourceFromAttributes({
            [ATTR_SERVICE_NAME]: SERVICE_NAME,
            [ATTR_SERVICE_VERSION]: APP_VERSION,
        });

        const collectorUrl = `https://${getApiHost()}/opentelemetry-service/v1/traces`;

        const exporter = new OTLPTraceExporter({
            url: collectorUrl,
            headers: getHeaders(),
        });

        const provider = new WebTracerProvider({
            resource,
            sampler: new DropNoisySpansSampler(),
            spanProcessors: [
                new IdentitySpanProcessor(),
                new HttpSpanNamingProcessor(),
                new RequestTrackingSpanProcessor(),
                // opentelemetry-service caps a POST at 100 kB and a 413 is not retried, so
                // the default batch of 512 spans would lose the whole window.
                new BatchSpanProcessor(exporter, {
                    maxExportBatchSize: MAX_SPANS_PER_EXPORT,
                    scheduledDelayMillis: 2000,
                }),
            ],
        });

        return provider;
    }

    function createMeterProvider(): MeterProvider {
        const resource = resourceFromAttributes({
            [ATTR_SERVICE_NAME]: SERVICE_NAME,
            [ATTR_SERVICE_VERSION]: APP_VERSION,
        });

        const collectorUrl = `https://${getApiHost()}/opentelemetry-service/v1/metrics`;

        const exporter = new OTLPMetricExporter({
            url: collectorUrl,
            headers: getHeaders(),
        });

        const provider = new MeterProvider({
            resource,
            readers: [
                new PeriodicExportingMetricReader({
                    exporter,
                    exportIntervalMillis: 60000, // Export every 60 seconds
                }),
            ],
        });

        return provider;
    }

    function startTelemetry(): void {
        if (tracerProvider != null) return;

        sendCollectorRequestsWithCredentials(`https://${getApiHost()}/opentelemetry-service/`);

        // Initialize tracing
        tracerProvider = createTracerProvider();
        tracerProvider.register({
            // Without this, context never survives an await, so every span starts
            // parentless and the page-load span gets no children at all.
            contextManager: new NavigationContextManager(),
            // Match resgate's composite propagator (W3C TraceContext + Baggage)
            // so trace context flows browser -> resgate -> NATS -> service-base-node.
            propagator: new CompositePropagator({
                propagators: [
                    new W3CTraceContextPropagator(),
                    new W3CBaggagePropagator(),
                ],
            }),
        });

        // No propagateTraceHeaderCorsUrls on purpose. Traefik's CORS middleware does not
        // list traceparent in accessControlAllowHeaders, so asking for it cross-origin
        // fails the preflight and the request is never sent ("Failed to fetch", status 0)
        // — which breaks every REST call on a *-story deploy. Same-origin requests, which
        // is dev and prod, propagate regardless. Restore this once traefik_cors_allow_headers
        // includes traceparent.

        // Exporting a span is itself an HTTP call; without this it would be traced,
        // producing spans about spans.
        const ignoreUrls: (string | RegExp)[] = [/\/opentelemetry-service\//];

        // Umami's analytics beacons would otherwise show up as spans and hold page-load spans open.
        if (appSettings.umamiScriptUrl !== '') ignoreUrls.push(new URL('api/send', appSettings.umamiScriptUrl).href);

        // Auto-instrument page load + outgoing HTTP so that RES calls and
        // other downstream work have a real parent span in the browser.
        // (UserInteraction is intentionally omitted — it requires zone.js.)
        unregisterInstrumentations = registerInstrumentations({
            tracerProvider,
            // ignoreNetworkEvents drops the nine DNS/TLS/TTFB events each span carries.
            // They are 41% of a span's bytes and say nothing extra about a same-origin call.
            instrumentations: [
                new DocumentLoadInstrumentation({ ignoreNetworkEvents: true }),
                new FetchInstrumentation({ ignoreUrls, ignoreNetworkEvents: true }),
                new XMLHttpRequestInstrumentation({ ignoreUrls, ignoreNetworkEvents: true }),
            ],
        });

        // Initialize metrics
        meterProvider = createMeterProvider();
        metrics.setGlobalMeterProvider(meterProvider);

        isEnabled.value = true;
        console.info('OpenTelemetry telemetry started');
    }

    function stopTelemetry(): void {
        // Unhook auto-instrumentations first so they don't try to create
        // spans against a provider that's being shut down.
        if (unregisterInstrumentations != null) {
            unregisterInstrumentations();
            unregisterInstrumentations = null;
        }

        abortPageLoad();

        if (restoreFetch != null) {
            restoreFetch();
            restoreFetch = null;
        }

        // Shutdown tracing
        if (tracerProvider != null) {
            tracerProvider.shutdown()
                .then(() => {
                    console.info('OpenTelemetry tracing stopped');
                })
                .catch((error: unknown) => {
                    console.error('Error shutting down OpenTelemetry tracing:', error);
                });
            tracerProvider = null;
        }

        // Shutdown metrics
        if (meterProvider != null) {
            meterProvider.shutdown()
                .then(() => {
                    console.info('OpenTelemetry metrics stopped');
                })
                .catch((error: unknown) => {
                    console.error('Error shutting down OpenTelemetry metrics:', error);
                });
            meterProvider = null;
        }

        isEnabled.value = false;
    }

    function init(): void {
        if (isInitialized) return;

        if (appSettings.enableOpentelemetry !== true) {
            console.info('OpenTelemetry is disabled');
            return;
        }

        isInitialized = true;

        // Watch login state to enable/disable telemetry
        watch(
            () => isLoggedIn.value,
            (loggedIn) => {
                if (loggedIn) {
                    startTelemetry();
                } else {
                    stopTelemetry();
                }
            },
            { immediate: true }
        );

        // Keep the identity stamped on new spans current.
        watch(
            [loginUser, selectedInstance],
            () => {
                setSpanIdentity(
                    loginUser.value?.Id ?? null,
                    selectedInstance.value?.SubDomain ?? null,
                    selectedInstance.value?.Name ?? null
                );
            },
            { immediate: true }
        );
    }

    function getMeter(name = SERVICE_NAME): Meter {
        return metrics.getMeter(name);
    }

    return {
        init,
        getTracer,
        getMeter,
    };
};
